<i18n src="./translations"></i18n>

<template>
  <div :class="classes" class="plp-product-26" :data-preload="[$options.name]">
    <div v-if="cleanResouceType" class="found-by-identifier">
      <Icon name="search_new" class="icon" width="20" height="20" />
      <span
        >{{ $t('plp-product-26.found_via_prefix') }}
        <strong>{{ foundByText }}</strong></span
      >
    </div>

    <div class="image-column qa-ProductCard__images">
      <div class="image-frame">
        <AdvancedImage
          v-if="product.brandLogoUrl"
          class="brand qa-ProductCard__logo"
          :src="product.brandLogoUrl"
          :alt="product.brandName"
          :config="{ q: 80, w: 100 }"
          data-testid="plp-brandLogo"
        />

        <AdvancedImage
          v-if="product.image.length"
          class="product-image qa-ProductCard__image"
          :src="product.image"
          :alt="product.image.alt || ''"
          :config="{ q: 80, w: 180 }"
          @image-click="productClick()"
        />
        <NoImageSvg
          v-else
          class="no-image"
          :lazy="true"
          :inline="false"
          @click="productClick()"
        />
      </div>
    </div>

    <div class="content-column">
      <div class="heading">
        <div
          class="title qa-ProductCard__showDetails"
          role="button"
          tabindex="0"
          :aria-label="`${product.brandName} ${product.name}`"
          data-testid="plp-product-details"
          @click="productClick()"
          @keydown.enter.prevent="productClick()"
          @keydown.space.prevent="productClick()"
        >
          {{ product.brandName }} {{ product.name }}
        </div>

        <span class="article-number">
          {{ $t('plp-product-26.article_number') }} {{ product.displaySku }}
          <span v-if="isSkuDisplayed" class="product-sku">
            | SKU:
            <input
              type="text"
              :value="product.sku"
              readonly
              :aria-label="$t('plp-product-26.product-sku')"
              @focus="$event.target.select()"
            />
          </span>
        </span>
      </div>

      <PdpVehicleNotification
        v-if="!product.isDropShipment && !isTyreProduct"
        class="vehicle-notification"
        :gtm-class-name="'gtm--plp-vehicle-notification'"
        :vehicle-compatibility="productVehicleCompatibility"
        :vehicle-name="vehicleName"
        :is-car-selected="isCarSelected"
        :car-attributes="carAttributes"
        :car-attributes-limit="carAttributesLimit"
        attributes-variant="list"
        default-expanded
      />

      <ProductInfoSections
        :product="product"
        variant="plp"
        :car-limit="carAttributesLimit"
        hide-car-attributes
        show-overflow-count
        hide-chevron
        :include-more-info="product.hasOeNumbers || product.hasCarTypes"
      >
        <template #hazardous-good="{ information }">
          <HazardousGood26
            :information="information"
            :documents="(product.documents && product.documents.items) || []"
            :show-compliance-details="isGermanLocale"
          />
        </template>

        <template #general-attributes="{ items, defaultLimit }">
          <PlpAttributes26 :regular="items" :default-limit="defaultLimit" />
        </template>

        <template #before-more-info-links>
          <a
            v-if="productBundles.length"
            class="product-set-link"
            data-testid="plp-product-set-link"
            :href="productSetUrl"
          >
            {{ $t('plp-product-26.product_set_link') }}
            <strong>
              ({{ productBundles.length }}
              {{ $t('plp-product-26.product_set_articles') }})
            </strong>
          </a>
        </template>

        <template #oe-link="{ oeNumbers, productId }">
          <PdpInfoOeLink
            class="more-info-link"
            :oe-numbers="oeNumbers"
            :product-id="productId"
            :label="$t('plp-product-26.oe_numbers_link')"
            hide-icon
          />
        </template>

        <template #links-separator>
          <span class="more-info-separator" aria-hidden="true">•</span>
        </template>

        <template #car-link="{ productId }">
          <PdpInfoCarLink
            class="more-info-link"
            :product-id="productId"
            :label="$t('plp-product-26.car_types_link')"
            hide-icon
          />
        </template>
      </ProductInfoSections>

      <TyreLabels
        is-minimal
        :attributes="product.attributes"
        :images="product.images"
      ></TyreLabels>
    </div>

    <div class="purchase-column">
      <PriceInfo21 ref="priceInfo" class="price" is-plp :product="product">
        <button
          class="to-wishlist"
          :class="{ '--wished': isInWishlist }"
          data-testid="pdp-add-to-wishlist-btn"
          :aria-label="
            isInWishlist
              ? $t('plp-product-26.remove_from_wishlist')
              : $t('plp-product-26.to_wishlist')
          "
          @click="handleWishlistClick"
        >
          <Icon
            :name="isInWishlist ? 'wishlist_filled_heart' : 'heart'"
            width="24"
            height="24"
            aria-hidden="true"
          />
        </button>
      </PriceInfo21>

      <template v-if="!isOTCSalesChannel">
        <QuantitySelector26
          class="quantity"
          :has-label="false"
          :has-vertical-divider="false"
          :product="product"
          :value="product.quantityAmount"
          :choices="product.quantityOptions"
          @onQuantityChange="handleQuantityChange($event)"
        />

        <Button
          class="in-the-cart"
          data-testid="pdp-add-to-cart-btn"
          :aria-label="$t('plp-product-26.cart-label')"
          :modifiers="['primary', 'full']"
          :on-click="handleCartClick"
          v-bind="
            getAddToCartButtonProps({
              add: $t('plp-product-26.cart.add'),
              added: $t('plp-product-26.cart.added'),
            })
          "
          :has-loading="addToCartState === AddToCartState.Loading"
        />
      </template>

      <Dialog
        ref="unavailableProductDialog"
        :click-outside="true"
        :title="$t('plp-product-26.availability.delayed.title')"
      >
        <div>
          {{
            $t('plp-product-26.availability.delayed.message', {
              '%customerServicePhoneNumber%': customerServicePhoneNumber,
            })
          }}
        </div>
      </Dialog>
    </div>

    <ProductAvailability :product-availability="product.productAvailability" />

    <AlternativeProductsDialog
      ref="alternativeProductsDialog"
      :parent-product="product"
      :handle-cancel="handleAlternativeProductCancel"
      :handle-confirm="handleAlternativeProductConfirm"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import globalMixin from 'Libs/mixins/globalMixin.js';
import addToCartMixin from 'Libs/mixins/addToCartMixin';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';
import AdvancedImage from 'Components/01-atoms/advanced-image/AdvancedImage.vue';
import ProductInfoSections from 'Src/components/02-molecules/product-info-sections/ProductInfoSections.vue';
import ProductAvailability from 'Components/02-molecules/product-availability/ProductAvailability';
import PriceInfo21 from 'Components/02-molecules/price-info-21/PriceInfo21.vue';
import NoImageSvg from 'Components/00-generated/NoImageSvg.vue';
import Dialog from 'Components/02-molecules/dialog/Dialog.vue';
import Icon from 'Components/00-generated/Icon';
import Button from 'Components/01-atoms/button/Button';
import TyreLabels from 'Components/02-molecules/tyre-labels/TyreLabels';
import { isTypeLabel } from 'Components/02-molecules/tyre-labels/models/tyre-labels';
import AlternativeProductsDialog from 'Components/03-organisms/alternative-products-dialog/AlternativeProductsDialog';
import QuantitySelector26 from 'Components/03-organisms/product-details-26/subcomponents/quantity-selector-26/QuantitySelector26.vue';
import HazardousGood26 from 'Components/02-molecules/hazardous-good-26/HazardousGood26.vue';
import PlpAttributes26 from 'Components/02-molecules/plp-attributes-26/PlpAttributes26.vue';
import PdpInfoOeLink from 'Components/02-molecules/product-info-sections/subcomponents/PdpInfoOeLink.vue';
import PdpInfoCarLink from 'Components/02-molecules/product-info-sections/subcomponents/PdpInfoCarLink.vue';

const CAR_ATTRIBUTES_LIMIT = 6;

export default {
  name: 'PlpProduct26',
  components: {
    AdvancedImage,
    ProductInfoSections,
    ProductAvailability,
    PriceInfo21,
    NoImageSvg,
    Dialog,
    Icon,
    Button,
    PdpVehicleNotification,
    TyreLabels,
    AlternativeProductsDialog,
    QuantitySelector26,
    HazardousGood26,
    PlpAttributes26,
    PdpInfoOeLink,
    PdpInfoCarLink,
  },
  mixins: [globalMixin, addToCartMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },
    position: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      carAttributesLimit: CAR_ATTRIBUTES_LIMIT,
    };
  },
  computed: {
    ...mapState('carSelection', {
      vehicleName: ({ data: { vehicle = {} } }) => {
        const { manufacturer, model, type, bodyCodes } = vehicle;
        const selectionName = `${manufacturer} ${model} ${type}`;
        if (bodyCodes && bodyCodes.length > 0) {
          return `${selectionName} (${[...bodyCodes].sort().join(', ')})`;
        }
        return selectionName;
      },
      isCarSelected: ({ data: { carDisplayName } }) => !!carDisplayName,
    }),
    ...mapState('core', {
      customerServicePhoneNumber: ({ salesChannel }) => {
        return salesChannel.config.customerServicePhoneNumber;
      },
      isEmployee: ({ isEmployee }) => isEmployee,
      locale: ({ salesChannel }) => salesChannel.locale,
    }),
    ...mapGetters('core', { isOTCSalesChannel: 'isOTCSalesChannel' }),
    ...mapGetters('wishlist', { checkIsInWishlist: 'isInWishlist' }),
    isInWishlist() {
      return this.checkIsInWishlist(this.product);
    },
    isGermanLocale() {
      return this.locale === 'de';
    },
    productBundles() {
      const setProductsItems = this.product.setProductsItems || [];
      const partsListItems = this.product.partsListItems || [];

      return setProductsItems.concat(partsListItems);
    },
    productSetUrl() {
      return this.product.productUrl
        ? `${this.product.productUrl}#product-set-content`
        : '#product-set-content';
    },
    isTyreProduct() {
      return this.itemTyreLabels.length > 0;
    },
    itemTyreLabels() {
      return (this.product.attributes || []).filter((attribute) =>
        isTypeLabel(attribute.id)
      );
    },
    productVehicleCompatibility() {
      return this.product?.pdpCarBar?.key || 'UNIVERSAL_PRODUCT';
    },
    attributes() {
      if (!this.product || !this.product.attributes) return [];

      return this.product.attributes
        .map((attribute) => ({
          label: attribute.title,
          info: attribute.value,
          isTop: attribute.isTop,
          id: attribute.id,
        }))
        .filter((attr) => !isTypeLabel(attr?.id));
    },
    carAttributes() {
      return this.attributes.filter(
        ({ isTop, id }) => isTop && id !== 'block_separation_8'
      );
    },
    isSkuDisplayed() {
      const availability = this.product?.productAvailability;
      if (!availability || typeof availability !== 'object') {
        return false;
      }

      const groups = Array.isArray(availability)
        ? availability
        : Object.values(availability);

      return groups.some((group) => Array.isArray(group) && group.length > 0);
    },
    cleanResouceType() {
      if (this.product.resultSourceType === 'display_sku') {
        return 'sku';
      }
      if (this.product.resultSourceType === 'competitor_part_number') {
        return 'trade_number';
      }

      return this.product.resultSourceType;
    },
    foundByText() {
      if (!this.cleanResouceType) {
        return null;
      }

      const reasonText = this.$t(
        `plp-product-26.found_reason.${this.cleanResouceType}`
      );

      if (this.cleanResouceType === 'oe_number') {
        return reasonText.replace(
          '%manufacturer%',
          this.product.resultSourceIdentifier
        );
      }

      return reasonText;
    },
  },
  methods: {
    ...mapActions('dataLayer', ['trackProductClick']),
    ...mapActions('wishlist', ['addToWishlist', 'removeFromWishlist']),
    handleWishlistClick() {
      if (this.isInWishlist) {
        this.removeFromWishlist(this.product);
      } else {
        this.addToWishlist({
          product: this.product,
          quantity: this.selectedQuantity,
          list: 'Product Listing Page',
        });
      }
    },
    handleCartClick() {
      this.handleAddToCart(this.selectedQuantity);
    },
    productClick() {
      this.trackProductClick({
        product: this.product,
        position: this.position,
      }).finally(() => {
        if (this.product.productUrl) {
          window.location.href = this.product.productUrl;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';
@import 'mixins';

.plp-product-26 {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-alto-sec);
  border-radius: 8px;
  padding: var(--space-2--half) var(--space-2);

  --hazardous-good-26-margin-top: var(--space-2);
  --hazardous-good-26-margin-bottom: var(--space-2);
  --hazardous-good-26-signal-icon-size: var(--space-3);

  @media #{$_mediaLUp} {
    display: grid;
    grid-template-columns: 2fr 3fr 2fr;
    align-items: flex-start;
    column-gap: var(--space-3);
    padding: var(--space-3);
  }

  @media #{$_mediaMDown} {
    padding: var(--space-2) var(--space-2) var(--space-3);
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: var(--space-2);
    grid-template-rows: auto auto min-content min-content; /* Keep price and availability rows sized to their content */
    grid-template-areas:
      'found found'
      'heading wishlist'
      'image price-tag'
      'image price-availability'
      'image price-texts'
      'cci cci'
      'attrs attrs'
      'tyre tyre'
      'bundles bundles'
      'quantity quantity'
      'cart cart'
      'availability availability';

    .content-column,
    .purchase-column {
      display: contents;
    }

    .price {
      --price-info-21-root-display: contents;
      --price-info-21-top-section-display: contents;
      --price-info-21-price-area-grid-area: price-tag;
      --price-info-21-actions-slot-grid-area: wishlist;
      --price-info-21-actions-slot-justify-self: end;
      --price-info-21-actions-slot-align-self: start;
      --price-info-21-availability-grid-area: price-availability;
      --price-info-21-price-texts-grid-area: price-texts;
      --price-info-21-main-price-font-size: var(--font-size-LXL);
    }
  }

  ::v-deep .more-info-links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1);
    margin-top: var(--space-2);

    @media #{$_mediaMDown} {
      padding: 0;
    }

    .more-info-link .info-link {
      margin-bottom: 0;
      font-size: var(--font-size-M);
    }

    .more-info-separator {
      color: var(--color-nero);
      font-size: var(--font-size-M);
    }
  }

  .product-set-link {
    display: block;
    font-size: var(--font-size-M);
    color: var(--color-nero);
    text-decoration: underline;
    margin-top: var(--space-2);

    strong {
      font-weight: bold;
    }

    @media #{$_mediaMDown} {
      grid-area: bundles;
      margin-bottom: var(--space-2);
    }
  }

  .image-column {
    flex-shrink: 0;
    width: 100%;

    @media #{$_mediaLUp} {
      width: 100%;
    }

    @media #{$_mediaMDown} {
      grid-area: image;
      width: 100%;
    }
  }

  .image-frame {
    position: relative;
    display: flex;
    justify-content: center;

    @media #{$_mediaLUp} {
      aspect-ratio: 1 / 1;
    }
  }

  .brand {
    position: absolute;
    top: 0;
    left: 0;
    height: 40px;
    padding: 4px;
    z-index: 1;
    background-color: var(--color-white);
    border-radius: 3px;

    &.advancedImage {
      width: 80px;

      @media #{$_mediaMDown} {
        width: var(--space-7);
      }
    }
  }

  .product-image {
    width: 100%;
    height: auto;
    cursor: pointer;

    @media #{$_mediaLUp} {
      height: 100%;
    }

    @media #{$_mediaMDown} {
      width: auto;
      max-height: 140px;
    }
  }

  .no-image {
    max-height: 126px;
  }

  .content-column {
    flex-grow: 1;
    text-align: left;
    width: 100%;
    min-width: 0;

    @media #{$_mediaLUp} {
      width: auto;
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }
  }

  .heading {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    @media #{$_mediaMDown} {
      grid-area: heading;
      grid-column: 1 / -1;
      padding: 0 var(--space-5) var(--space-2) 0;
    }
  }

  .title {
    color: var(--color-black);
    display: block;
    cursor: pointer;
    margin-bottom: var(--space-1);
    font-weight: bold;
    font-size: var(--font-size-ML);

    &:hover {
      text-decoration: underline;
    }
  }

  .article-number {
    cursor: text;
    font-size: var(--font-size-M);
    line-height: 17px;
    display: block;
    color: var(--color-black);
  }

  .product-sku {
    input {
      border: 0;
      background: transparent;
      font-size: var(--font-size-M);
      color: var(--color-black);
      width: 90px;
    }
  }

  .found-by-identifier {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    margin:
      calc(var(--space-2--half) * -1) calc(var(--space-2) * -1)
      var(--space-2--half);
    padding: var(--space-1--half) var(--space-2);
    background: var(--color-silver-fog);
    border-radius: 8px 8px 0 0;
    font-size: var(--font-size-SM);
    color: var(--color-black);

    @media #{$_mediaLUp} {
      grid-column: 1 / -1;
      margin: calc(var(--space-3) * -1) calc(var(--space-3) * -1) var(--space-3);
      padding: var(--space-1--half) var(--space-3);
    }

    @media #{$_mediaMDown} {
      grid-area: found;
    }

    ::v-deep .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
    }

    ::v-deep strong {
      font-weight: bold;
    }
  }

  .vehicle-notification {
    @media #{$_mediaMDown} {
      grid-area: cci;
      margin-top: var(--space-2);
    }
  }

  ::v-deep .product-info-sections {
    @media #{$_mediaMDown} {
      grid-area: attrs;
      padding: var(--space-2) 0 var(--space-2) 0;
    }
  }

  ::v-deep .tyre-labels {
    @media #{$_mediaMDown} {
      grid-area: tyre;
      margin-bottom: var(--space-3);
    }
  }

  ::v-deep .product-stock-debug {
    @media #{$_mediaLUp} {
      grid-column: 1 / -1;
    }

    @media #{$_mediaMDown} {
      grid-area: availability;
    }
  }

  .purchase-column {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: var(--space-2);

    @media #{$_mediaLUp} {
      width: 100%;
      flex-shrink: 0;
      margin-top: 0;
    }

    @media #{$_mediaMDown} {
      display: contents;
      margin-top: 0;
    }
  }

  .price {
    padding-bottom: var(--space-1--half);
    gap: var(--space-1);

    @media #{$_mediaMDown} {
      padding-bottom: 0;
    }
  }

  .to-wishlist {
    cursor: pointer;
    padding: 0;
    border: 0;
    background: transparent;
    flex-shrink: 0;

    ::v-deep .icon {
      color: var(--color-kfzteile24-secondary);
    }

    &.--wished ::v-deep .icon {
      fill: var(--color-kfzteile24-secondary);
    }
  }

  .quantity {
    width: 100%;
    margin-top: var(--space-1);

    @media #{$_mediaMDown} {
      grid-area: quantity;
      padding-top: 0 0 var(--space-2) 0;
      margin: 0;
    }

    ::v-deep .input-wrapper {
      width: 100%;
    }

    ::v-deep .input {
      flex: 1;
      min-width: 0;
      height: 48px;
      padding-top: 0;
    }
  }

  .in-the-cart {
    height: 48px;
    margin-top: var(--space-1);
    flex-grow: 1;

    @media #{$_mediaMDown} {
      grid-area: cart;
      margin-top: var(--space-2);
    }
  }

  ::v-deep .plp-description-21__description {
    padding: 0;
  }
}
</style>
